"use client"

import React, { useState, useEffect } from "react"
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  Button,
  Tooltip,
  InputLabel,
  Snackbar,
} from "@mui/material"
import { People, PictureAsPdf, Description } from "@mui/icons-material"

// Import separate components
import EmployeeSection from "./EmployeeSection.jsx"
import TrainingSection from "./EmpTrainingSection.jsx"
import ContractEmployeeSection from "./EmpContractempsection.jsx"
import BonafideSection from "./EmpBonfiedsection.jsx"
import AdvanceSalarySection from "./EmpSalaryReport.jsx"

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

const EmployeeManagementReport = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [period, setPeriod] = useState("Yearly")
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()])
  const [exporting, setExporting] = useState({ pdf: false, doc: false })
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [reportData, setReportData] = useState({
    employees: [],
    trainings: [],
    contractEmployees: [],
    bonafideRequests: [],
    salaryRequests: [],
    contracts: [],
    roles: [],
    departments: [],
    employeeTrainings: [],
    contractDetails: [],
    certificates: [],
  })

  // Fetch all data function
  const fetchAllData = async (period, year) => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        alert("No authentication token found. Please log in.")
        return { reportData: null, error: "No authentication token found" }
      }
  

      const yearParam = period.toLowerCase() === 'yearly' ? `&year=${year}` : ''
      const response = await fetch(`${API_BASE_URL}/api/employee-report/summary?period=${period}${yearParam}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch report summary: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.status === "success" && result.data) {
        const data = result.data
        const reportData = {
          employees: Array.isArray(data.employees) ? data.employees : [],
          trainings: Array.isArray(data.trainings) ? data.trainings : [],
          contractEmployees: Array.isArray(data.contractEmployees) ? data.contractEmployees : [],
          bonafideRequests: Array.isArray(data.bonafideRequests) ? data.bonafideRequests : [],
          salaryRequests: Array.isArray(data.salaryRequests) ? data.salaryRequests : [],
          contracts: Array.isArray(data.contracts) ? data.contracts : [],
          roles: Array.isArray(data.roles) ? data.roles : [],
          departments: Array.isArray(data.departments) ? data.departments : [],
          employeeTrainings: Array.isArray(data.employeeTrainings) ? data.employeeTrainings : [],
          contractDetails: Array.isArray(data.contractDetails) ? data.contractDetails : [],
          certificates: Array.isArray(data.certificates) ? data.certificates : [],
        }
        return { reportData, error: null }
      } else {
        throw new Error("Invalid response status or data missing")
      }
    } catch (error) {
      //console.error("Error fetching data:", error)
      return {
        reportData: {
          employees: [],
          trainings: [],
          contractEmployees: [],
          bonafideRequests: [],
          salaryRequests: [],
          contracts: [],
          roles: [],
          departments: [],
          employeeTrainings: [],
          contractDetails: [],
          certificates: [],
        },
        error: error.message,
      }
    }
  }

  // Fetch available years
  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(`${API_BASE_URL}/api/employee-report/years`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        if (data.years && data.years.length > 0) {
          setAvailableYears(data.years)
          if (!data.years.includes(selectedYear)) {
            setSelectedYear(data.years[0])
          }
        }
      }
    } catch (e) {
      //console.error("Error fetching available years:", e)
    }
  }

  useEffect(() => {
    fetchAvailableYears()
  }, [])

  // Fetch data on component mount and period change
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        const result = await fetchAllData(period, selectedYear)

        if (result.reportData) {
          setReportData(result.reportData)
        }

        if (result.error) {
          setError(result.error)
        }
      } catch (err) {
        //console.error("Error loading data:", err)
        setError(`Failed to load report data: ${err.message}`)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [period, selectedYear])

  // Calculate statistics
  const getStatistics = () => {
    // Ensure all data is arrays to prevent filter errors
    const employees = Array.isArray(reportData.employees) ? reportData.employees : []
    const trainings = Array.isArray(reportData.trainings) ? reportData.trainings : []
    const contractEmployees = Array.isArray(reportData.contractEmployees) ? reportData.contractEmployees : []
    const bonafideRequests = Array.isArray(reportData.bonafideRequests) ? reportData.bonafideRequests : []
    const salaryRequests = Array.isArray(reportData.salaryRequests) ? reportData.salaryRequests : []
    const contracts = Array.isArray(reportData.contracts) ? reportData.contracts : []

 

    const stats = {
      totalEmployees: employees.length,
      totalTrainings: trainings.length,
      totalContractEmployees: contractEmployees.length,
      totalBonafideRequests: bonafideRequests.length,
      totalSalaryRequests: salaryRequests.length,
      totalContracts: contracts.length,
      totalRecords:
        employees.length +
        trainings.length +
        contractEmployees.length +
        bonafideRequests.length +
        salaryRequests.length +
        contracts.length,

      // Training statistics
      completedTrainings: trainings.filter((t) => {
        const status = (t.status || "").toLowerCase()
        return status === "completed" || status === "complete"
      }).length,
      ongoingTrainings: trainings.filter((t) => {
        const status = (t.status || "").toLowerCase()
        return ["ongoing", "in progress", "active", "started"].includes(status)
      }).length,

      // Bonafide request statistics
      approvedBonafide: bonafideRequests.filter((b) => {
        const status = (b.status || "").toLowerCase()
        return status === "approved" || status === "accepted"
      }).length,
      pendingBonafide: bonafideRequests.filter((b) => {
        const status = (b.status || "").toLowerCase()
        return status === "pending" || status === "waiting" || status === "submitted"
      }).length,

      // Salary request statistics
      approvedSalary: salaryRequests.filter((s) => {
        const status = (s.status || "").toLowerCase()
        return status === "approved" || status === "accepted"
      }).length,
      pendingSalary: salaryRequests.filter((s) => {
        const status = (s.status || "").toLowerCase()
        return status === "pending" || status === "waiting" || status === "submitted"
      }).length,

      // Contract statistics
      activeContracts: contracts.filter((c) => {
        const status = (c.status || "").toLowerCase()
        return status === "active" || status === "ongoing" || status === "current"
      }).length,

      // Employee status
      activeEmployees: employees.filter((e) => {
        const status = (e.status || e.employmentStatus || e.employee_status || "active").toLowerCase()
        return status === "active" || status === "employed" || status === "working"
      }).length,
    }

    return stats
  }

  // Export to PDF using backend API
  const handleExportPDF = async () => {
    try {
      setExporting({ ...exporting, pdf: true })
      const token = localStorage.getItem("token")


      // Create URL with query parameters
      const url = new URL(`${API_BASE_URL}/api/employee-report/export/pdf`)
      url.searchParams.append("activeTab", activeTab)
      url.searchParams.append("period", period)
      if (period.toLowerCase() === 'yearly') {
        url.searchParams.append("year", selectedYear)
      }
      if (token) {
        url.searchParams.append("token", token)
      }

      // Open in new tab to download
      window.open(url.toString(), "_blank")

      // Set loading state for a few seconds
      setTimeout(() => {
        setExporting({ ...exporting, pdf: false })
        setSnackbar({
          open: true,
          message: "PDF exported successfully",
          severity: "success",
        })
      }, 3000)
    } catch (error) {
      //console.error("PDF Export Error:", error)
      setSnackbar({
        open: true,
        message: `Failed to export PDF: ${error.message || "Unknown error"}`,
        severity: "error",
      })
      setExporting({ ...exporting, pdf: false })
    }
  }

  // Export to DOC using backend API
  const handleExportDoc = async () => {
    try {
      setExporting({ ...exporting, doc: true })
      const token = localStorage.getItem("token")


      // Create URL with query parameters
      const url = new URL(`${API_BASE_URL}/api/employee-report/export/doc`)
      url.searchParams.append("activeTab", activeTab)
      url.searchParams.append("period", period)
      if (period.toLowerCase() === 'yearly') {
        url.searchParams.append("year", selectedYear)
      }
      if (token) {
        url.searchParams.append("token", token)
      }

      // Open in new tab to download
      window.open(url.toString(), "_blank")

      // Set loading state for a few seconds
      setTimeout(() => {
        setExporting({ ...exporting, doc: false })
        setSnackbar({
          open: true,
          message: "Document exported successfully",
          severity: "success",
        })
      }, 3000)
    } catch (error) {
      //console.error("DOC Export Error:", error)
      setSnackbar({
        open: true,
        message: `Failed to export document: ${error.message || "Unknown error"}`,
        severity: "error",
      })
      setExporting({ ...exporting, doc: false })
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const retryFetch = async () => {
    setError(null)
    try {
      setLoading(true)
      const data = await fetchAllData(period, selectedYear)
      setReportData(data.reportData)
      if (data.error) {
        setError(data.error)
      }
    } catch (err) {
      setError(`Failed to load report data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const StatCard = ({ title, value, icon, color = "#1976d2" }) => (
    <Card
      sx={{
        height: "120px",
        display: "flex",
        alignItems: "center",
        bgcolor: "white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: "1px solid #f0f0f0",
        borderRadius: "8px",
      }}
    >
      <CardContent sx={{ display: "flex", alignItems: "center", width: "100%", p: 2 }}>
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            bgcolor: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          {React.cloneElement(icon, { sx: { color: "white", fontSize: 24 } })}
        </Box>
        <Box>
          <Typography variant="h3" fontWeight="bold" color="#333" sx={{ fontSize: "2rem" }}>
            {value}
          </Typography>
          <Typography variant="body1" color="#666" sx={{ fontSize: "0.9rem" }}>
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Report Data...
        </Typography>
      </Box>
    )
  }

  const stats = getStatistics()

  return (
    <Box sx={{ p: 3, border: "1px solid", borderColor: "grey.300", boxShadow: 2, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}
      >
        <Typography variant="h4" fontWeight="600" color="#333" align="center">
          Employee Management Reports
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="period-select-label">Period</InputLabel>
            <Select
              labelId="period-select-label"
              id="period-select"
              value={period}
              label="Period"
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ bgcolor: "white", borderRadius: 2 }}
            >
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Quarterly">Quarterly</MenuItem>
              <MenuItem value="Yearly">Yearly</MenuItem>
              <MenuItem value="All">All</MenuItem>
            </Select>
          </FormControl>

          {period.toLowerCase() === 'yearly' && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="year-select-label">Year</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={{ bgcolor: "white", borderRadius: 2 }}
              >
                {availableYears.map(yr => (
                  <MenuItem key={yr} value={yr}>
                    {yr} {yr === new Date().getFullYear() ? '(Current)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Tooltip title="Export to PDF">
            <Button
              onClick={handleExportPDF}
              variant="outlined"
              startIcon={exporting.pdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
              disabled={exporting.pdf || exporting.doc}
              sx={{
                padding: "10px 16px",
                fontSize: "0.9rem",
                textTransform: "none",
                borderRadius: "8px",
                borderWidth: 2,
                borderColor: "primary",
                color: "primary",
                "&:hover": {
                  borderColor: "#1d4ed8",
                  bgcolor: "#eff6ff",
                },
              }}
            >
              {exporting.pdf ? "Exporting..." : "Export PDF"}
            </Button>
          </Tooltip>

          <Tooltip title="Export to Document">
            <Button
              onClick={handleExportDoc}
              variant="outlined"
              startIcon={exporting.doc ? <CircularProgress size={16} /> : <Description />}
              disabled={exporting.pdf || exporting.doc}
              sx={{
                padding: "10px 16px",
                fontSize: "0.9rem",
                textTransform: "none",
                borderRadius: "8px",
                borderWidth: 2,
                borderColor: "primary",
                color: "primary",
                "&:hover": {
                  borderColor: "#1d4ed8",
                  bgcolor: "#eff6ff",
                },
              }}
            >
              {exporting.doc ? "Exporting..." : "Export DOC"}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
          <Button onClick={retryFetch} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

    

      {/* Tabs */}
      <Paper sx={{ bgcolor: "white", borderRadius: "8px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
               <Tabs
          value={activeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: "1px solid #e9ecef",
            "& .MuiTab-root": {
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "0.85rem",
              color: "#666",
            },
            "& .Mui-selected": {
              color: "#1976d2",
            },
          }}
        >
          <Tab label="EMPLOYEES" />
          <Tab label="TRAININGS" />
          <Tab label="CONTRACT EMPLOYEES" />
          <Tab label="BONAFIDE" />
          <Tab label="ADVANCE SALARY" />
        </Tabs>


        <Box sx={{ p: 3 }}>
          {activeTab === 0 && <EmployeeSection data={reportData} stats={stats} period={period} />}
          {activeTab === 1 && <TrainingSection data={reportData.trainings} period={period} />}
          {activeTab === 2 && <ContractEmployeeSection data={reportData.contractEmployees} period={period} />}
          {activeTab === 3 && <BonafideSection data={reportData.bonafideRequests} period={period} />}
          {activeTab === 4 && <AdvanceSalarySection data={reportData.salaryRequests} period={period} />}
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  )
}

export default EmployeeManagementReport
